import { useEffect, useMemo, useRef, useState } from "react";
import YouTube from "react-youtube";
import Player from "@vimeo/player";

import { getYouTubeVideoId } from "../../../../../utils/getYouTubeVideoId";

import Button from "../../ButtonComponent";
import HeaderModule from "../../ModuleHeaderComponent";
import Shimmer from "../../ShimmerComponent";

import { BUTTON_VARIANTS } from "../../constant";

import { useGetTranslatedText } from "../../queries";
import { getTranslatedText } from "../../../../../utils/getTranslatedText";

import styles from "./styles.module.scss";

const getVideoUrl = (videoData = {}) => {
  const possibleUrls = [
    videoData?.videoUrl,
    videoData?.video_url,
    videoData?.vimeoUrl,
    videoData?.vimeo_url,
    videoData?.videoLink,
    videoData?.video_link,
    videoData?.contentUrl,
    videoData?.content_url,
    videoData?.fileUrl,
    videoData?.file_url,
    videoData?.url,
    videoData?.link,
    videoData?.video,
  ];

  return (
    possibleUrls.find((url) => typeof url === "string" && url.trim())?.trim() ||
    ""
  );
};

const getVimeoVideoId = (url = "") => {
  const videoUrl = String(url).trim();

  if (!videoUrl) return null;

  if (/^\d+$/.test(videoUrl)) return videoUrl;

  const decodedUrl = decodeURIComponent(videoUrl);

  const match = decodedUrl.match(
    /(?:vimeo\.com\/(?:.*\/)?|player\.vimeo\.com\/video\/)(\d+)(?:[/?#]|$)/i,
  );

  return match?.[1] || null;
};

const InclusiveEducationVideo = ({
  inclusiveEducationContent,
  isLoading,
  handleClick,
  handleBackClick,
  isPending,
  isFetching,
}) => {
  const [isVideoCompleted, setIsVideoCompleted] = useState(false);

  const playerRef = useRef(null);
  const iframeRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);

  const languageId = localStorage.getItem("l_id") || 1;

  const { data: translatedTextResponse } = useGetTranslatedText(languageId);

  const { data: translatedText = [] } = translatedTextResponse || {};

  const { data: inclusiveEducationContentData } =
    inclusiveEducationContent || {};

  const { response } = inclusiveEducationContentData || {};

  const videoData = response?.videos?.[0];

  const videoStatus = Number(videoData?.videoStatus || 0);

  const videoUrl = getVideoUrl(videoData);

  const normalizedVideoUrl = videoUrl.toLowerCase();

  const isYoutube =
    normalizedVideoUrl.includes("youtube.com") ||
    normalizedVideoUrl.includes("youtu.be");

  const isVimeo =
    normalizedVideoUrl.includes("vimeo.com") ||
    normalizedVideoUrl.includes("player.vimeo.com");

  const youtubeVideoId = useMemo(() => {
    if (!isYoutube) return null;

    return getYouTubeVideoId(videoUrl.replace("watch/", "watch?v="));
  }, [isYoutube, videoUrl]);

  const vimeoVideoId = useMemo(() => {
    if (!isVimeo) return null;

    return getVimeoVideoId(videoUrl);
  }, [isVimeo, videoUrl]);

  const vimeoPlayerId = vimeoVideoId ? `inclusive-vimeo-${vimeoVideoId}` : "";

  useEffect(() => {
    setIsVideoCompleted(false);
  }, [videoUrl]);

  useEffect(() => {
    if (!isVimeo || !iframeRef.current) return;

    const player = new Player(iframeRef.current);

    player.on("ended", () => {
      console.log("Vimeo video completed");
      setIsVideoCompleted(true);
    });

    return () => {
      player.off("ended");
      player.destroy();
    };
  }, [isVimeo, vimeoVideoId]);

  useEffect(() => {
    playerRef.current = new Player(iframeRef.current);

    playerRef.current.on("play", () => {
      setIsPlaying(true);
    });

    playerRef.current.on("pause", () => {
      setIsPlaying(false);
    });

    return () => {
      playerRef.current?.destroy();
    };
  }, []);

  const isButtonDisabled =
    !(videoStatus === 1 || isVideoCompleted) || isPending;

  return (
    <>
      <HeaderModule
        title={videoData?.title}
        className={styles["header-title"]}
        onBackClick={handleBackClick}
      />

      {isLoading || isFetching ? (
        <Shimmer
          width="65%"
          height="75vh"
          borderRadius="14px"
          style={{ margin: "20px auto" }}
        />
      ) : (
        <div className={styles["video-container-wrapper"]}>
          <div className={styles["video-player"]}>
            {isYoutube && youtubeVideoId ? (
              <YouTube
                videoId={youtubeVideoId}
                className={styles["video-container"]}
                iframeClassName={styles["video-container"]}
                opts={{
                  width: "100%",
                  height: "100%",
                  playerVars: {
                    controls: 0,
                    rel: 0,
                    disablekb: 1,
                    modestbranding: 1,
                  },
                }}
                onEnd={() => {
                  console.log("YouTube video completed");
                  setIsVideoCompleted(true);
                }}
              />
            ) : isVimeo && vimeoVideoId ? (
              <iframe
                ref={iframeRef}
                title={videoData?.title || "Vimeo Video"}
                src={`https://player.vimeo.com/video/${vimeoVideoId}?title=0&byline=0&portrait=0&controls=0`}
                className={styles["video-container"]}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <iframe
                title={videoData?.title || "Video"}
                src={videoUrl}
                className={styles["video-container"]}
                allowFullScreen
              />
            )}
          </div>
        </div>
      )}

      <div className={styles["bottom-wrapper"]}>
        <Button
          label={getTranslatedText(
            translatedText,
            "label_Continue",
            "Continue",
          )}
          buttonVariant={BUTTON_VARIANTS.PRIMARY}
          disabled={isButtonDisabled}
          className={styles["button-class"]}
          onClick={handleClick}
        />
      </div>
    </>
  );
};

export default InclusiveEducationVideo;
